import React, { memo } from 'react';
import type { StepNode } from '@segmently/schema-models/v2';
import type { OnActionCallback, OnEventCallback } from '../../../types/events';
import type { TemplateVariables } from '../../../utils/contentResolver';
import type { PreviewMode } from '../../../types/previewMode';
import { Image } from '../../shared/Image';
import { Text } from '../../shared/Text';
import { ActionBar } from '../../shared/ActionBar';
import { Background } from '../../shared/Background';
import { HeaderBar } from '../../shared/HeaderBar';
import styles from './HeroContent.module.css';

export interface HeroContentProps {
  step: StepNode;
  locale: string;
  variables?: TemplateVariables;
  resolvedScreenValues?: Record<string, string>;
  onAction?: OnActionCallback;
  onEvent?: OnEventCallback;
  editMode?: boolean;
  previewMode?: PreviewMode;
}

export const HeroContent = memo(function HeroContent({
  step,
  locale,
  variables,
  resolvedScreenValues,
  onAction,
  onEvent: _onEvent,
  editMode = false,
  previewMode,
}: HeroContentProps): React.ReactElement {
  const { content } = step;

  const ep = editMode || previewMode === 'live' ? 'content' : undefined;

  const isHeroMode = (step as any).subtype === 'heroSlot2';

  const getFlexOrders = () => {
    const subtype = (step as any).subtype;
    switch (subtype) {
      case 'heroSlot3':
        return { image: 4, title: 1, subtitle: 2 };
      case 'heroSlot4':
        return { image: 2, title: 1, subtitle: 3 };
      default:
        return { image: 1, title: 2, subtitle: 3 };
    }
  };

  const orders = getFlexOrders();

  return (
    <div
      className={`${styles.container}${previewMode === 'live' ? ` ${styles.liveMode}` : ''}${isHeroMode ? ` ${styles.noPaddingTop}` : ''}${editMode ? ' screen-view--edit-mode' : ''}`}
      data-testid="view-hero-content"
    >
      <Background
        backdrop={content?.canvas?.background}
        locale={locale}
        editPath={ep ? `${ep}.canvas.background` : undefined}
        previewMode={previewMode}
      />

      <HeaderBar
        header={content?.header}
        locale={locale}
        variables={variables}
        step={step}
        onAction={onAction}
        editPath={ep ? `${ep}.header` : undefined}
        absolute={isHeroMode}
      />

      <div className={`${styles.content} ${isHeroMode ? '' : styles.xPaddings}`}>
        <Image
          image={content?.hero}
          locale={locale}
          alt="Hero image"
          editPath={ep ? `${ep}.hero` : undefined}
          style={{ order: orders.image }}
          previewMode={previewMode}
        />
        <Text
          label={content?.copy?.title}
          locale={locale}
          variables={variables}
          resolvedScreenValues={resolvedScreenValues}
          editPath={ep ? `${ep}.copy.title` : undefined}
          className={isHeroMode ? styles.xPaddings : undefined}
          style={{ order: orders.title }}
        />
        <Text
          label={content?.copy?.subtitle}
          locale={locale}
          variables={variables}
          resolvedScreenValues={resolvedScreenValues}
          editPath={ep ? `${ep}.copy.subtitle` : undefined}
          className={isHeroMode ? styles.xPaddings : undefined}
          style={{ order: orders.subtitle }}
        />
      </div>

      <ActionBar
        actionBar={content?.actionBar}
        locale={locale}
        variables={variables}
        step={step}
        onAction={onAction}
        editPath={ep ? `${ep}.actionBar` : undefined}
      />
    </div>
  );
});

HeroContent.displayName = 'HeroContent';
